import React, { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  FiPieChart,
  FiUsers,
  FiPackage,
  FiBarChart2,
  FiSettings,
  FiLogOut,
  FiShield,
  FiX
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';

/**
 * AdminSidebar Component
 * Left navigation sidebar for admin panel matching BBBooks merchant design
 * Now with mobile responsive functionality
 */
const AdminSidebar = ({ isOpen, onClose }) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (onClose) {
      onClose();
    }
  }, [location.pathname, onClose]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: FiPieChart, label: 'Dashboard' },
    { path: '/admin/merchants', icon: FiUsers, label: 'Merchants' },
    { path: '/admin/subscriptions', icon: FiPackage, label: 'Subscriptions' },
    { path: '/admin/analytics', icon: FiBarChart2, label: 'Analytics' },
    { path: '/admin/settings', icon: FiSettings, label: 'Settings' },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`w-64 h-screen bg-white shadow-lg flex flex-col fixed left-0 top-0 z-50 transition-transform duration-300 lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Brand Logo */}
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                <FiShield className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-display font-bold text-neutral-900 block">
                  BBBooks
                </span>
                <span className="text-xs text-neutral-500">Admin Panel</span>
              </div>
            </div>
            {/* Close button - visible only on mobile */}
            <button
              onClick={onClose}
              className="lg:hidden text-neutral-400 hover:text-neutral-900 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
        </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-button transition-all duration-200 ${
                  isActive
                    ? 'bg-primary-500 text-white shadow-button'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-neutral-500'}`} />
                  <span className={`font-medium ${isActive ? 'text-white' : 'text-neutral-700'}`}>
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Logout Button */}
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 rounded-button text-neutral-600 hover:bg-neutral-100 transition-colors w-full"
          >
            <FiLogOut className="w-5 h-5 text-neutral-500" />
            <span className="font-medium text-neutral-700">Sign Out</span>
          </button>
        </div>
      </nav>

      {/* Admin Info Section */}
      <div className="p-4 m-4 bg-gradient-to-br from-primary-500 to-primary-700 rounded-card text-white">
        <div className="text-center mb-3">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-3">
            <FiShield className="w-6 h-6" />
          </div>
          <h3 className="font-bold text-sm mb-1">
            {user?.username || 'Admin'}
          </h3>
          <p className="text-xs opacity-90">
            Administrator Access
          </p>
        </div>
        <div className="text-center pt-3 border-t border-white border-opacity-20">
          <p className="text-xs opacity-75">Version 1.0.5</p>
        </div>
      </div>
      </div>
    </>
  );
};

export default AdminSidebar;
